<template>
  <div v-if="showContent && !!fiscalInfo">
    <div class="regulatory-update-fiscal-info">
      <HeadingDisplay
        title="Informações fiscais"
        head="Revisão de dados"
        class="regulatory-update-fiscal-info__heading"
      />

      <div class="regulatory-update-fiscal-info__toggle-section">
        <ToggleSwitch v-model="isUsPersonToggle" hide-divider>
          <BaseText size="p3" :color="NebraskaColors.elementPrimary">
            Tenho cidadania americana (US Person)
          </BaseText>
        </ToggleSwitch>

        <BaseText size="p4">
          Marque se nasceu nos EUA, possui cidadania americana, tem green card
          ou é residente fiscal no país por tempo de permanência.
        </BaseText>

        <InputText
          v-if="isUsPersonToggle"
          v-model="fiscalInfo.usPerson.itin"
          name="itinOrSsn"
          label="Código do ITIN ou SSN"
          :disabled="isItinOrSsnDisabled"
          :helper-text="getErrorMessage('itinOrSsn')"
          :status="getInputStatus('itinOrSsn')"
          class="regulatory-update-fiscal-info__itin-input"
          :mask="ssnMask"
          :max-length="11"
          @focus="removeError('itinOrSsn')"
          @input="validateInRealTime"
        />

        <Checkbox
          v-if="isUsPersonToggle"
          v-model="hasNoUsTinToggle"
          class="regulatory-update-fiscal-info__no-us-tin-checkbox"
          @change="onNoUsTinChange"
        >
          Declaro, sob as penas da lei, não possuir número TIN dos EUA aplicável
          à minha situação
        </Checkbox>

        <InputSelect
          v-if="showNoUsTinJustification"
          v-model="fiscalInfo.usPerson.reasonOfAbsence"
          name="noUsTinJustification"
          label="Motivo da ausência do TIN"
          required
          :options="noUsTinJustificationOptions"
          :helper-text="getErrorMessage('noUsTinJustification')"
          :status-type="getInputStatus('noUsTinJustification')"
          @change="onNoUsTinJustificationChange"
        />
      </div>

      <div class="regulatory-update-fiscal-info__divider" />

      <div class="regulatory-update-fiscal-info__toggle-section">
        <ToggleSwitch v-model="hasForeignTaxResidenceToggle" hide-divider>
          <BaseText size="p3" :color="NebraskaColors.elementPrimary">
            Tenho residência fiscal fora do Brasil
          </BaseText>
        </ToggleSwitch>
        <BaseText size="p4">
          Marque se você declara impostos em outro país além do Brasil.
        </BaseText>

        <InputSelect
          v-if="hasForeignTaxResidenceToggle"
          v-model="fiscalInfo.taxResidence.country"
          class="regulatory-update-fiscal-info__country-select"
          label="País de residência fiscal"
          required
          :options="countriesList"
          :helper-text="getErrorMessage('taxResidenceCountry')"
          :status-type="getInputStatus('taxResidenceCountry')"
          @change="onTaxResidenceCountryChange"
        />

        <InputText
          v-if="showNifField"
          v-model="fiscalInfo.taxResidence.nif"
          :disabled="isNifDisabled"
          name="nifNumber"
          label="Número do NIF"
          :helper-text="getErrorMessage('nifNumber')"
          :status="getInputStatus('nifNumber')"
          @focus="removeError('nifNumber')"
          @input="onNifInput"
        />

        <Checkbox
          v-if="hasForeignTaxResidenceToggle"
          v-model="hasNoNifToggle"
          class="regulatory-update-fiscal-info__no-nif-checkbox"
          @change="onNoNifChange"
        >
          Declaro, sob as penas da lei, não possuir número NIF no país informado
          aplicável à minha situação
        </Checkbox>

        <InputSelect
          v-if="showNoNifJustification"
          v-model="fiscalInfo.taxResidence.reasonOfAbsence"
          name="noNifJustification"
          label="Motivo da ausência do NIF"
          required
          :options="noNifJustificationOptions"
          :helper-text="getErrorMessage('noNifJustification')"
          :status-type="getInputStatus('noNifJustification')"
          @change="onNoNifJustificationChange"
        />
      </div>

      <ButtonContainer class="regulatory-update-fiscal-info__footer">
        <template #button-primary>
          <ButtonPrimary
            :loading="isButtonLoading"
            size="large"
            label="Continuar"
            @click="handleButtonClick"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  HeadingDisplay,
  InputSelect,
  InputText,
  Checkbox,
  ToggleSwitch,
  BaseText,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { YupValidationErrors } from '@/modules/common/helpers/yup-validation';

import {
  RegulatoryUpdateCustomerUsPerson,
  RegulatoryUpdateCustomerTaxResidence,
  InputStatusType,
  Country
} from '../../types';
import { getCountries } from '../../services';
import yupValidation from './validate';
import { trackEvent, trackError, trackView } from './track-events';
import { EventKnownProperties } from '@warrenbrasil/web-analytics';
import { useRegulatoryUpdateStore } from '@/modules/account/views/RegulatoryUpdate/store/regulatoryUpdateStore';

const NO_US_TIN_JUSTIFICATION_OPTIONS = [
  {
    label: 'Estou em processo de obtenção do TIN (SSN ou ITIN)',
    value: 'Estou em processo de obtenção do TIN (SSN ou ITIN)'
  },
  {
    label: 'Sou menor de idade e ainda não possuo SSN/ITIN',
    value: 'Sou menor de idade e ainda não possuo SSN/ITIN'
  },
  {
    label:
      'Estou em processo formal de renúncia da cidadania americana e ainda não possuo TIN',
    value:
      'Estou em processo formal de renúncia da cidadania americana e ainda não possuo TIN'
  }
];
const NO_NIF_JUSTIFICATION_OPTIONS = [
  {
    label: 'O país de residência fiscal não emite NIF/TIN para pessoas físicas',
    value: 'O país de residência fiscal não emite NIF/TIN para pessoas físicas'
  },
  {
    label:
      'Não sou obrigado(a) a possuir um NIF/TIN, conforme as regras do meu país de residência fiscal',
    value:
      'Não sou obrigado(a) a possuir um NIF/TIN, conforme as regras do meu país de residência fiscal'
  },
  {
    label: 'Estou em processo de obtenção do NIF/TIN',
    value: 'Estou em processo de obtenção do NIF/TIN'
  },
  {
    label: 'Não possuo NIF/TIN e não consigo obtê-lo de forma razoável',
    value: 'Não possuo NIF/TIN e não consigo obtê-lo de forma razoável'
  }
];

@Component({
  components: {
    HeadingDisplay,
    InputSelect,
    InputText,
    Checkbox,
    BaseText,
    ToggleSwitch,
    ButtonContainer,
    ButtonPrimary
  }
})
export default class RegulatoryUpdateFiscalInfo extends Vue {
  @Prop({ type: Boolean, required: true })
  public showContent!: boolean;

  @Prop({ type: String, default: 'regulatoryUpdateFiscalInfo' })
  public cameFrom!: string;

  @Emit()
  private setIsLoading(_isLoading: boolean) {}

  @Emit()
  private setHasError(_hasError: boolean) {}

  @Emit()
  private goToNextPage() {}

  @Emit()
  private notifyError(_message?: string) {}

  @Emit()
  private changeTracker(
    _newTracker: (remainingProps: EventKnownProperties) => void
  ) {}

  public readonly NebraskaColors = NebraskaColors;
  public fiscalInfo: {
    usPerson: RegulatoryUpdateCustomerUsPerson;
    taxResidence: RegulatoryUpdateCustomerTaxResidence;
  } | null = null;

  public countriesList: Country[] = [];
  public isButtonLoading = false;

  public errors: YupValidationErrors = {};

  public noUsTinJustificationOptions = NO_US_TIN_JUSTIFICATION_OPTIONS;
  public noNifJustificationOptions = NO_NIF_JUSTIFICATION_OPTIONS;

  public isUsPersonToggle = false;
  public hasForeignTaxResidenceToggle = false;
  public hasNoUsTinToggle = false;
  public hasNoNifToggle = false;

  private previousUsPersonToggle = false;
  private previousForeignTaxResidenceToggle = false;

  public ssnMask = '###-##-####';

  private readonly regulatoryUpdateStore = useRegulatoryUpdateStore();

  mounted() {
    this.fetchFiscalInfo();
    this.changeTracker(trackEvent);
  }

  private async fetchFiscalInfo() {
    this.setIsLoading(true);
    this.setHasError(false);

    try {
      const [countries] = await Promise.all([getCountries()]);

      trackView();

      this.fiscalInfo = {
        usPerson: this.regulatoryUpdateStore.usPerson || {
          itin: '',
          reasonOfAbsence: ''
        },
        taxResidence: this.regulatoryUpdateStore.taxResidence || {
          country: '',
          nif: '',
          reasonOfAbsence: ''
        }
      };

      this.countriesList = countries;

      this.initializeToggles();
    } catch (_e) {
      this.setHasError(true);
    }

    this.setIsLoading(false);
  }

  private initializeToggles() {
    if (!this.fiscalInfo) return;

    this.isUsPersonToggle = !!(
      this.fiscalInfo.usPerson.itin || this.fiscalInfo.usPerson.reasonOfAbsence
    );

    this.hasForeignTaxResidenceToggle = !!(
      this.fiscalInfo.taxResidence.country ||
      this.fiscalInfo.taxResidence.nif ||
      this.fiscalInfo.taxResidence.reasonOfAbsence
    );

    this.hasNoUsTinToggle = !!this.fiscalInfo.usPerson.reasonOfAbsence;
    this.hasNoNifToggle = !!this.fiscalInfo.taxResidence.reasonOfAbsence;

    this.previousUsPersonToggle = this.isUsPersonToggle;
    this.previousForeignTaxResidenceToggle = this.hasForeignTaxResidenceToggle;
  }

  public getInputStatus(key: string) {
    const errorKeyMap: { [key: string]: string } = {
      itinOrSsn: 'usPerson.itin',
      noUsTinJustification: 'usPerson.reasonOfAbsence',
      taxResidenceCountry: 'taxResidence.country',
      nifNumber: 'taxResidence.nif',
      noNifJustification: 'taxResidence.reasonOfAbsence'
    };

    const actualKey = errorKeyMap[key] || key;

    if (
      (actualKey.startsWith('usPerson.') && !this.isUsPersonToggle) ||
      (actualKey.startsWith('taxResidence.') &&
        !this.hasForeignTaxResidenceToggle)
    ) {
      return InputStatusType.Default;
    }

    return this.errors[actualKey]
      ? InputStatusType.Error
      : InputStatusType.Default;
  }

  public getErrorMessage(key: string) {
    const errorKeyMap: { [key: string]: string } = {
      itinOrSsn: 'usPerson.itin',
      noUsTinJustification: 'usPerson.reasonOfAbsence',
      taxResidenceCountry: 'taxResidence.country',
      nifNumber: 'taxResidence.nif',
      noNifJustification: 'taxResidence.reasonOfAbsence'
    };

    const actualKey = errorKeyMap[key] || key;

    if (
      (actualKey.startsWith('usPerson.') && !this.isUsPersonToggle) ||
      (actualKey.startsWith('taxResidence.') &&
        !this.hasForeignTaxResidenceToggle)
    ) {
      return '';
    }

    return this.errors[actualKey] ? this.errors[actualKey][0] : '';
  }

  public removeError(key: string) {
    const errorKeyMap: { [key: string]: string } = {
      itinOrSsn: 'usPerson.itin',
      noUsTinJustification: 'usPerson.reasonOfAbsence',
      taxResidenceCountry: 'taxResidence.country',
      nifNumber: 'taxResidence.nif',
      noNifJustification: 'taxResidence.reasonOfAbsence'
    };

    const actualKey = errorKeyMap[key] || key;
    delete this.errors[actualKey];
  }

  public get showNifField() {
    return this.hasForeignTaxResidenceToggle;
  }

  public get showNoNifJustification() {
    return this.hasForeignTaxResidenceToggle && this.hasNoNifToggle;
  }

  public get showNoUsTinJustification() {
    return this.isUsPersonToggle && this.hasNoUsTinToggle;
  }

  public get isItinOrSsnDisabled() {
    return this.hasNoUsTinToggle;
  }

  public get isNifDisabled() {
    return this.hasNoNifToggle;
  }

  @Watch('isUsPersonToggle')
  onIsUsPersonToggleChanged(newVal: boolean) {
    if (!newVal) {
      this.fiscalInfo!.usPerson.itin = '';
      this.fiscalInfo!.usPerson.reasonOfAbsence = '';
      this.hasNoUsTinToggle = false;
      this.removeError('itinOrSsn');
      this.removeError('noUsTinJustification');
      this.validateErrors();
    }
  }

  @Watch('hasForeignTaxResidenceToggle')
  onHasForeignTaxResidenceToggleChanged(newVal: boolean) {
    if (!newVal) {
      this.fiscalInfo!.taxResidence.country = '';
      this.fiscalInfo!.taxResidence.nif = '';
      this.fiscalInfo!.taxResidence.reasonOfAbsence = '';
      this.hasNoNifToggle = false;
      this.removeError('taxResidenceCountry');
      this.removeError('nifNumber');
      this.removeError('noNifJustification');
      this.validateErrors();
    }
  }

  public onTaxResidenceCountryChange() {
    this.removeError('taxResidenceCountry');

    if (
      this.fiscalInfo!.taxResidence.country === 'US' ||
      this.fiscalInfo!.taxResidence.country === 'Estados Unidos'
    ) {
      if (this.fiscalInfo!.usPerson.itin) {
        this.fiscalInfo!.taxResidence.nif = this.fiscalInfo!.usPerson.itin;
      }
    }

    this.validateInRealTime();
  }

  public onNoNifChange() {
    if (this.hasNoNifToggle) {
      this.fiscalInfo!.taxResidence.nif = '';
      this.removeError('nifNumber');
    } else {
      this.fiscalInfo!.taxResidence.reasonOfAbsence = '';
      this.removeError('noNifJustification');
    }
    this.validateInRealTime();
  }

  public onNoUsTinChange() {
    if (this.hasNoUsTinToggle) {
      this.fiscalInfo!.usPerson.itin = '';
      this.removeError('itinOrSsn');
    } else {
      this.fiscalInfo!.usPerson.reasonOfAbsence = '';
      this.removeError('noUsTinJustification');
    }
    this.validateInRealTime();
  }

  private validateInRealTime() {
    this.$forceUpdate();
  }

  public onNoUsTinJustificationChange() {
    this.removeError('noUsTinJustification');
    this.validateInRealTime();
  }

  public onNoNifJustificationChange() {
    this.removeError('noNifJustification');
    this.validateInRealTime();
  }

  public onNifInput(value: string) {
    this.fiscalInfo!.taxResidence.nif = value;

    this.removeError('nifNumber');
    this.validateInRealTime();
  }

  public async handleButtonClick() {
    await this.validateErrors();
    if (yupValidation.isValid()) {
      if (this.isUsPersonToggle) {
        this.regulatoryUpdateStore.setUsPerson(this.fiscalInfo!.usPerson);
      }
      if (this.hasForeignTaxResidenceToggle) {
        this.regulatoryUpdateStore.setTaxResidence(
          this.fiscalInfo!.taxResidence
        );
      }
      this.goToNextPage();
    }

    Object.values(this.errors).forEach((v: string[]) => trackError(v[0]));
  }

  private async validateErrors() {
    try {
      await yupValidation.validate({
        usPerson: {
          isUsPersonToggle: this.isUsPersonToggle,
          itin: this.fiscalInfo?.usPerson.itin || '',
          reasonOfAbsence: this.fiscalInfo?.usPerson.reasonOfAbsence || ''
        },
        taxResidence: {
          hasForeignTaxResidenceToggle: this.hasForeignTaxResidenceToggle,
          country: this.fiscalInfo?.taxResidence.country || '',
          nif: this.fiscalInfo?.taxResidence.nif || '',
          reasonOfAbsence: this.fiscalInfo?.taxResidence.reasonOfAbsence || ''
        }
      });
    } catch (e) {}

    this.errors = yupValidation.getAllErrors();
  }
}
</script>

<style lang="less" scoped>
.regulatory-update-fiscal-info {
  padding-bottom: @size-spacing-x1800;
  display: flex;
  flex-direction: column;
  gap: @size-spacing-x500;

  &__heading {
    margin-bottom: @size-spacing-x800;
  }

  &__toggle-section {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x300;
  }

  &__divider {
    height: 1px;
    background: #ebe7e6;
    margin: @size-spacing-x400 0;
  }

  &__no-nif-checkbox,
  &__no-us-tin-checkbox {
    margin: @size-spacing-x400 0;
  }

  &__footer {
    position: fixed;
    left: 0;
    bottom: 0;
    background-color: @background-secondary;
    z-index: 1;
  }

  &__country-select {
    margin-top: @size-spacing-x400;
    margin-bottom: @size-spacing-x400;
  }

  &__itin-input {
    margin-top: @size-spacing-x400;
  }
}
</style>
